import React, { Component, createContext, useContext, createRef } from 'react'
import { intersectionObserver } from 'utilities/dom'
import { get, post } from 'api'
import debounce from 'lodash.debounce'
import isEqual from 'react-fast-compare'
import { size } from 'utilities/object'
// import { getDate, getTime } from 'utilities/date-time'
// import { addYears } from 'date-fns'

const SurveysContext = createContext()
SurveysContext.displayName = 'Surveys'

export default class SurveysProvider extends Component {
    constructor(props) {
        super(props)

        const { eternal = true } = props

        this.fetchController = new AbortController()
        this.fetchDebounced = debounce(this.fetch, 100, { maxWait: 500, leading: false, trailing: true })

        this.intersectionObserver = intersectionObserver(this.onIntersect)
        this.pagingDefaults = pagingDefaults(props?.paging)

        this.flash = createRef()

        this.state = {
            surveys: [],
            paging: this.pagingDefaults(),
            eternal,
            ...(eternal ? { intersecter: this.intersectionObserver.ref } : null),

            fetchSurveys: this.fetch,
            addSurvey: this.add,

            hasFetched: false,
            autoFetch: false,
            fetching: false,
            loading: false,

            flash: this.flash,
            clearFlash: this.clearFlash,
        }
    }

    componentDidMount() {
        const {
            fetchAccess = true,
            fetchOnMount = true
        } = this.props

        if(fetchAccess && fetchOnMount) {
            this.fetchDebounced()
        }
    }

    componentDidUpdate(props) {
        const pagingChanged = !isEqual(props.paging, this.props.paging)

        const { fetchAccess: previousFetchAccess = true } = props
        const { fetchAccess = true } = this.props
        const fetchAccessGained = !previousFetchAccess && fetchAccess

        const state = {}

        if(pagingChanged) {
            this.pagingDefaults = pagingDefaults(this.props?.paging)
            state.paging = this.pagingDefaults()
        }

        this.setState(size(state) ? state : null, () => {
            if(fetchAccessGained) {
                this.fetchDebounced()
            }
        })
    }

    componentWillUnmount() {
        this.fetchController.abort()
        this.intersectionObserver.destroy()
    }

    fetch = async (force = false) => {
        const {
            fetching,
            paging,
            eternal,
            autoFetch,
            hasFetched
        } = this.state

        if((fetching || (hasFetched && !eternal)) && !force) {
            return
        }

        if(force || fetching) {
            this.fetchController.abort()
            this.fetchController = new AbortController()
        }

        this.setState({
            fetching: true,
            ...(autoFetch ? { loading: true } : null)
        })

        const nextPaging = {
            offset: hasFetched ? paging.offset + paging.limit : 0,
            limit: paging.limit
        }

        const { ok, response } = await get({
            path: '/surveys',
            params: nextPaging,
            signal: this.fetchController.signal
        })

        if(ok && response) {
            this.setState(({ surveys: previousSurveys }) => {
                const surveys = [
                    ...previousSurveys,
                    ...response.items
                ]

                return {
                    surveys,
                    paging: {
                        ...paging,
                        ...nextPaging,
                        hasNextPage: response.items?.length && surveys.length < response.total
                    },
                    hasFetched: true,
                    autoFetch: this.props.autoFetch || (!!previousSurveys.length && hasFetched),
                    fetching: false,
                    loading: false
                }
            })
        } else {
            this.setState({
                hasFetched: true,
                autoFetch: false,
                fetching: false,
                loading: false
            })
        }
    }

    // start: {
    //     date: getDate(addYears(now, 69)),
    //     time: getTime(now, { units: 'minutes' }),
    //     zone: Intl.DateTimeFormat().resolvedOptions().timeZone
    // }

    add = async body => {
        const { ok, response: survey } = await post({
            path: '/surveys',
            body
        })

        if(ok && survey) {
            this.flash.current = survey

            this.setState(({ surveys }) => ({
                surveys: [
                    survey,
                    ...surveys
                ]
            }))
        }

        return { ok, response: survey }
    }

    onIntersect = () => {
        const {
            eternal,
            loading,
            paging,
            autoFetch
        } = this.state

        if(!eternal) {
            return
        }

        if(!loading && paging.hasNextPage && autoFetch) {
            this.fetchDebounced()
        }
    }

    clearFlash = () => void this.setState(({ surveys }) => {
        this.flash.current = null

        return {
            surveys: [...surveys]
        }
    })

    render() {
        const { children = null } = this.props

        return (
            <SurveysContext.Provider value={this.state}>
                {(typeof children === 'function') && children(this.state)}
                {(typeof children !== 'function') && children}
            </SurveysContext.Provider>
        )
    }
}

const pagingDefaults = (overrides = {}) => () => ({
    offset: 0,
    limit: 10,
    ...overrides,
    hasNextPage: false
})

export const useSurveys = () => useContext(SurveysContext)